// 要素の選択（段階 C の2 の 7g、2026-10-26。前は d3 v4.10.2 の d3.select・d3.selectAll。d3-selection, Copyright 2010-2017 Mike Bostock, BSD-3-Clause の決まりに合わせた）。
// クラシック版のエンジンの SVG の入れ物（chibi.svgMake など）・前任者の処理（charat.legacy.js）・セーブスロット・広場の部品が、前と同じ書き方で使う。
// 要素に持たせるデータ（__data__）と、付けたイベントの記録（__on: { type, name, value, listener, capture }）は d3 と同じ名前・形
// （確かめる道具 tools/chibi-unify/check-classic-actions.mjs が __on で押せるものを探す。d3 の付けたものと混ざっても読み合える）
import { transitionOf, type Transition } from './transition.js';

type Datum = unknown;
type Group = (Element | null)[];
type Parent = Element | Document | EnterNode | null;
type Node = Element & { __data__?: Datum; __on?: OnEntry[] };
type ValueFn<T> = (this: Element, d: Datum, i: number, group: Group) => T;
type Val<T> = T | ValueFn<T>;
interface OnEntry {
  type: string;
  name: string;
  value: (this: Element, d: Datum, i: number, group: Group) => void;
  listener: EventListener;
  capture: boolean | AddEventListenerOptions;
}

export const NAMESPACES: Record<string, string> = {
  svg: 'http://www.w3.org/2000/svg',
  xhtml: 'http://www.w3.org/1999/xhtml',
  xlink: 'http://www.w3.org/1999/xlink',
  xml: 'http://www.w3.org/XML/1998/namespace',
  xmlns: 'http://www.w3.org/2000/xmlns/',
};
/** "xlink:href" → { space, local }（接頭辞の無い名前はそのまま。d3.namespace） */
export function namespace(name: string): string | { space: string; local: string } {
  let prefix = (name += '');
  const i = prefix.indexOf(':');
  if (i >= 0 && (prefix = name.slice(0, i)) !== 'xmlns') name = name.slice(i + 1);
  return Object.prototype.hasOwnProperty.call(NAMESPACES, prefix) ? { space: NAMESPACES[prefix], local: name } : name;
}

/** 要素を作る（親と同じ名前空間。"svg:g" のように書けばその名前空間） */
function creator(name: string): (this: Element | EnterNode) => Element {
  const full = namespace(name);
  if (typeof full !== 'string') return function () {
    return (this.ownerDocument as Document).createElementNS(full.space, full.local);
  };
  return function () {
    const doc = this.ownerDocument as Document, uri = this.namespaceURI;
    return uri === NAMESPACES.xhtml && doc.documentElement.namespaceURI === NAMESPACES.xhtml ? doc.createElement(full) : doc.createElementNS(uri, full);
  };
}

/** CSS の値（インラインの値、無ければ計算後の値。d3 の selection.style(名前)） */
export function styleValue(node: Element, name: string): string {
  return (node as HTMLElement).style.getPropertyValue(name) || (node.ownerDocument.defaultView as Window).getComputedStyle(node, null).getPropertyValue(name);
}

/** データを付けたときの、まだ無い要素の場所（d3 の EnterNode。append でここに要素を作る） */
class EnterNode {
  ownerDocument: Document;
  namespaceURI: string | null;
  _next: Element | null = null;
  _parent: Element;
  __data__: Datum;
  constructor(parent: Element, datum: Datum) {
    this.ownerDocument = parent.ownerDocument;
    this.namespaceURI = parent.namespaceURI;
    this._parent = parent;
    this.__data__ = datum;
  }
  appendChild(child: Element): Element {
    return this._parent.insertBefore(child, this._next);
  }
  insertBefore(child: Element, next: Element | null): Element {
    return this._parent.insertBefore(child, next);
  }
  querySelector(selector: string): Element | null {
    return this._parent.querySelector(selector);
  }
  querySelectorAll(selector: string): NodeListOf<Element> {
    return this._parent.querySelectorAll(selector);
  }
}

/** いまの d3.event にあたるもの（クリックなどを呼んでいる間の出来事） */
let currentEvent: Event | null = null;
export const event = (): Event | null => currentEvent;

function parseTypenames(typenames: string): { type: string; name: string }[] {
  return (typenames + '')
    .trim()
    .split(/^|\s+/)
    .map((t) => {
      let name = '';
      const i = t.indexOf('.');
      if (i >= 0) (name = t.slice(i + 1)), (t = t.slice(0, i));
      return { type: t, name };
    });
}

export class Selection {
  _enter?: Group[];
  _exit?: Group[];
  constructor(
    public _groups: Group[],
    public _parents: Parent[],
  ) {}

  // ---- 選ぶ ----

  /** 要素ごとに中の最初の 1 つ（無ければ空き。データは親から写す） */
  select(selector: string | ValueFn<Element | null>): Selection {
    const sel = typeof selector === 'function' ? selector : function (this: Element) {
      return this.querySelector(selector);
    };
    const subgroups: Group[] = [];
    for (const group of this._groups) {
      const sub: Group = new Array(group.length);
      for (let i = 0; i < group.length; i++) {
        const node = group[i] as Node | null;
        let subnode: Node | null;
        if (node && (subnode = sel.call(node, node.__data__, i, group) as Node | null)) {
          if ('__data__' in node) subnode.__data__ = node.__data__;
          sub[i] = subnode;
        }
      }
      subgroups.push(sub);
    }
    return new Selection(subgroups, this._parents);
  }
  /** 要素ごとに中の合うもの全部（要素ごとに組を分ける） */
  selectAll(selector: string | ValueFn<ArrayLike<Element>>): Selection {
    const sel = typeof selector === 'function' ? selector : function (this: Element) {
      return this.querySelectorAll(selector);
    };
    const subgroups: Group[] = [], parents: Parent[] = [];
    for (const group of this._groups) {
      for (let i = 0; i < group.length; i++) {
        const node = group[i] as Node | null;
        if (node) {
          subgroups.push(Array.prototype.slice.call(sel.call(node, node.__data__, i, group)));
          parents.push(node);
        }
      }
    }
    return new Selection(subgroups, parents);
  }
  /** 合うものだけ（セレクターか関数） */
  filter(match: string | ValueFn<boolean>): Selection {
    const fn = typeof match === 'function' ? match : function (this: Element) {
      return this.matches(match);
    };
    const subgroups: Group[] = [];
    for (const group of this._groups) {
      const sub: Group = [];
      for (let i = 0; i < group.length; i++) {
        const node = group[i];
        if (node && fn.call(node, (node as Node).__data__, i, group)) sub.push(node);
      }
      subgroups.push(sub);
    }
    return new Selection(subgroups, this._parents);
  }

  // ---- 中身 ----

  each(fn: ValueFn<void>): this {
    for (const group of this._groups) for (let i = 0; i < group.length; i++) {
      const node = group[i];
      if (node) fn.call(node, (node as Node).__data__, i, group);
    }
    return this;
  }
  /** fn(この選択, ...args) を呼ぶ */
  call(fn: (sel: Selection, ...args: any[]) => void, ...args: any[]): this {
    fn(this, ...args);
    return this;
  }
  node(): Element | null {
    for (const group of this._groups) for (const node of group) if (node) return node;
    return null;
  }
  nodes(): Element[] {
    const out: Element[] = [];
    this.each(function () {
      out.push(this);
    });
    return out;
  }
  size(): number {
    let n = 0;
    this.each(() => {
      ++n;
    });
    return n;
  }
  empty(): boolean {
    return !this.node();
  }

  // ---- 属性・CSS・class・文字 ----

  /** 属性の値（無ければ null。前の d3 の型に合わせて string とする） */
  attr(name: string): string;
  attr(name: string, value: Val<unknown>): this;
  attr(name: string, value?: Val<unknown>): this | string | null {
    const full = namespace(name);
    if (arguments.length < 2) {
      const node = this.node()!;
      return typeof full === 'string' ? node.getAttribute(full) : node.getAttributeNS(full.space, full.local);
    }
    return this.each(function (d, i, group) {
      const v = typeof value === 'function' ? (value as ValueFn<unknown>).call(this, d, i, group) : value;
      if (typeof full === 'string') {
        if (v == null) this.removeAttribute(full);
        else this.setAttribute(full, v as string);
      } else if (v == null) this.removeAttributeNS(full.space, full.local);
      else this.setAttributeNS(full.space, full.local, v as string);
    });
  }
  style(name: string): string;
  style(name: string, value: Val<unknown>, priority?: string): this;
  style(name: string, value?: Val<unknown>, priority?: string): this | string {
    if (arguments.length < 2) return styleValue(this.node()!, name);
    const pr = priority == null ? '' : priority;
    return this.each(function (d, i, group) {
      const v = typeof value === 'function' ? (value as ValueFn<unknown>).call(this, d, i, group) : value;
      const st = (this as HTMLElement).style;
      if (v == null) st.removeProperty(name);
      else st.setProperty(name, v as string, pr);
    });
  }
  classed(names: string): boolean;
  classed(names: string, value: Val<boolean>): this;
  classed(names: string, value?: Val<boolean>): this | boolean {
    const list = (names + '').trim().split(/^|\s+/);
    if (arguments.length < 2) {
      const cl = this.node()!.classList;
      for (const n of list) if (!cl.contains(n)) return false;
      return true;
    }
    return this.each(function (d, i, group) {
      const on = typeof value === 'function' ? (value as ValueFn<boolean>).call(this, d, i, group) : value;
      for (const n of list) {
        if (on) this.classList.add(n);
        else this.classList.remove(n);
      }
    });
  }
  text(): string;
  text(value: Val<unknown>): this;
  text(value?: Val<unknown>): this | string {
    if (arguments.length < 1) return this.node()!.textContent as string;
    return this.each(function (d, i, group) {
      const v = typeof value === 'function' ? (value as ValueFn<unknown>).call(this, d, i, group) : value;
      this.textContent = v == null ? '' : (v as string);
    });
  }
  html(): string;
  html(value: Val<unknown>): this;
  html(value?: Val<unknown>): this | string {
    if (arguments.length < 1) return this.node()!.innerHTML;
    return this.each(function (d, i, group) {
      const v = typeof value === 'function' ? (value as ValueFn<unknown>).call(this, d, i, group) : value;
      this.innerHTML = v == null ? '' : (v as string);
    });
  }
  property(name: string): unknown;
  property(name: string, value: Val<unknown>): this;
  property(name: string, value?: Val<unknown>): this | unknown {
    if (arguments.length < 2) return (this.node() as unknown as Record<string, unknown>)[name];
    return this.each(function (d, i, group) {
      const v = typeof value === 'function' ? (value as ValueFn<unknown>).call(this, d, i, group) : value;
      if (v == null) delete (this as unknown as Record<string, unknown>)[name];
      else (this as unknown as Record<string, unknown>)[name] = v;
    });
  }

  // ---- 足す・外す ----

  /** 要素ごとに中の最後に作る（データは親から） */
  append(name: string | ValueFn<Element>): Selection {
    const create = typeof name === 'function' ? name : creator(name);
    return this.select(function (d, i, group) {
      return this.appendChild((create as ValueFn<Element>).call(this, d, i, group));
    });
  }
  /** 要素ごとに before の前に作る（before が無ければ最後） */
  insert(name: string | ValueFn<Element>, before?: string | ValueFn<Element | null>): Selection {
    const create = typeof name === 'function' ? name : creator(name);
    const find = before == null ? () => null : typeof before === 'function' ? before : function (this: Element) {
      return this.querySelector(before);
    };
    return this.select(function (d, i, group) {
      return this.insertBefore((create as ValueFn<Element>).call(this, d, i, group), (find as ValueFn<Element | null>).call(this, d, i, group) || null);
    });
  }
  remove(): this {
    return this.each(function () {
      const parent = this.parentNode;
      if (parent) parent.removeChild(this);
    });
  }

  // ---- データ（順番で組にする。d3 の selection.data のキーなしの形） ----

  datum(): Datum;
  datum(value: Datum): this;
  datum(value?: Datum): this | Datum {
    if (arguments.length < 1) return (this.node() as Node).__data__;
    return this.each(function () {
      (this as Node).__data__ = value;
    });
  }
  data(values: Datum[] | ((this: Parent, d: Datum, j: number, parents: Parent[]) => Datum[])): Selection {
    const valueOf = typeof values === 'function' ? values : () => values;
    const m = this._groups.length, update: Group[] = new Array(m), enter: Group[] = new Array(m), exit: Group[] = new Array(m);
    for (let j = 0; j < m; ++j) {
      const parent = this._parents[j], group = this._groups[j], groupLength = group.length;
      const data = valueOf.call(parent, parent && (parent as Node).__data__, j, this._parents), dataLength = data.length;
      const enterGroup = (enter[j] = new Array(dataLength)) as unknown as (EnterNode | undefined)[];
      const updateGroup = (update[j] = new Array(dataLength));
      const exitGroup = (exit[j] = new Array(groupLength));
      let i = 0;
      for (; i < dataLength; ++i) {
        const node = group[i] as Node | null;
        if (node) {
          node.__data__ = data[i];
          updateGroup[i] = node;
        } else enterGroup[i] = new EnterNode(parent as Element, data[i]);
      }
      for (; i < groupLength; ++i) if (group[i]) exitGroup[i] = group[i];
      // 作る要素を、あとに続く今の要素の前に入れられるように
      for (let i0 = 0, i1 = 0; i0 < dataLength; ++i0) {
        const previous = enterGroup[i0];
        if (previous) {
          if (i0 >= i1) i1 = i0 + 1;
          let next: Element | null = null;
          while (!(next = updateGroup[i1]) && ++i1 < dataLength);
          previous._next = next || null;
        }
      }
    }
    const out = new Selection(update, this._parents);
    out._enter = enter;
    out._exit = exit;
    return out;
  }
  enter(): Selection {
    return new Selection(this._enter || this._groups.map((g) => new Array(g.length)), this._parents);
  }
  exit(): Selection {
    return new Selection(this._exit || this._groups.map((g) => new Array(g.length)), this._parents);
  }

  // ---- イベント ----

  /** 付ける（listener に null で外す）・付けたものを返す（listener を省く） */
  on(typenames: string): ((this: Element, d: Datum, i: number, group: Group) => void) | undefined;
  on(typenames: string, listener: ((this: Element, d: Datum, i: number, group: Group) => void) | null, capture?: boolean | AddEventListenerOptions): this;
  on(typenames: string, listener?: ((this: Element, d: Datum, i: number, group: Group) => void) | null, capture?: boolean | AddEventListenerOptions): unknown {
    const typenamesList = parseTypenames(typenames);
    if (arguments.length < 2) {
      const on = (this.node() as Node).__on;
      if (on)
        for (const o of on)
          for (const t of typenamesList) if (t.type === o.type && t.name === o.name) return o.value;
      return undefined;
    }
    const cap = capture == null ? false : capture;
    for (const t of typenamesList) {
      this.each(function (_d, i, group) {
        const node = this as Node;
        const on = node.__on;
        if (!listener) {
          if (!on) return;
          let k = -1;
          for (let j = 0; j < on.length; ++j) {
            const o = on[j];
            if ((!t.type || o.type === t.type) && o.name === t.name) node.removeEventListener(o.type, o.listener, o.capture);
            else on[++k] = o;
          }
          if (++k) on.length = k;
          else delete node.__on;
          return;
        }
        const wrapped: EventListener = function (this: Element, ev: Event) {
          const ev0 = currentEvent;
          currentEvent = ev;
          try {
            listener.call(this, (this as Node).__data__, i, group);
          } finally {
            currentEvent = ev0;
          }
        };
        if (on)
          for (const o of on) {
            if (o.type === t.type && o.name === t.name) {
              node.removeEventListener(o.type, o.listener, o.capture);
              node.addEventListener(o.type, (o.listener = wrapped), (o.capture = cap));
              o.value = listener;
              return;
            }
          }
        node.addEventListener(t.type, wrapped, cap);
        const o: OnEntry = { type: t.type, name: t.name, value: listener, listener: wrapped, capture: cap };
        if (!on) node.__on = [o];
        else on.push(o);
      });
    }
    return this;
  }

  // ---- 動き ----

  transition(name?: string): Transition {
    return transitionOf(this, name);
  }
}

/** 文書の中の最初の 1 つ・要素そのもの（d3.select） */
export function select(target: string | Element | Window | Document | null): Selection {
  return typeof target === 'string'
    ? new Selection([[document.querySelector(target)]], [document.documentElement])
    : new Selection([[target as Element | null]], [null]);
}
/** 文書の中の合うもの全部・要素の並び（d3.selectAll） */
export function selectAll(target: string | ArrayLike<Element> | null): Selection {
  return typeof target === 'string'
    ? new Selection([Array.prototype.slice.call(document.querySelectorAll(target))], [document.documentElement])
    : new Selection([target == null ? [] : Array.prototype.slice.call(target)], [null]);
}
